import { ApiClient } from "../api-client";
import schemaValidators from "./schema-validators";

export class ConnectionCheckClient extends ApiClient<typeof schemaValidators> {
	constructor(config: ApiClient["config"]) {
		super(config, schemaValidators);
	}

	// Returns upload speed in bits per second
	async #badConnectionCheck(uploadByteSize = 8e5, timeout = 5000, numFetchRequest = 8) {
		if (globalThis.XMLHttpRequest) {
			const data = new Blob(["0".repeat(uploadByteSize)]);
			return new Promise<number>((resolve, _reject) => {
				const xhr = new XMLHttpRequest();

				const connectionCheckUrl = `${this.config.verityOrigin}/connection/check`;
				let startTime: number;
				let lastLoaded: number = 0;
				xhr.open("POST", connectionCheckUrl, true);
				xhr.timeout = timeout;

				xhr.upload.addEventListener("loadstart", function (ev) {
					if (ev.loaded === 0) {
						startTime = performance.now();
					}
				});

				xhr.upload.addEventListener("progress", (ev) => {
					if (ev.lengthComputable) {
						lastLoaded = ev.loaded;
					}
				});

				const finalize = () => {
					const duration = performance.now() - startTime; // ms
					resolve((lastLoaded * 8) / (duration / 1000)); // bps
				};
				xhr.upload.addEventListener("loadend", finalize);
				xhr.ontimeout = finalize;

				xhr.send(data);
			});
		} else {
			/**
			 * XMLHttpRequest can't be used from a service worker, so this is the fallback.
			 * Fetch doesn't tell upload progress, so we split large upload into smaller chunks to get a rough estimate.
			 * We considered using an iframe/content script to run the XMLHttpRequest but it seemed like a lot of work just to get the user upload speed
			 */
			const controller = new AbortController();

			const startTime = performance.now();
			let lastFetchEndTime = startTime;
			let lastFetchDuration = 0;

			const smallByteSize = uploadByteSize / numFetchRequest;
			let uploadSuccessCount = 0;

			const data = new Blob(["0".repeat(smallByteSize)]);

			setTimeout(() => {
				controller.abort();
			}, timeout);

			try {
				for (let i = 0; i < numFetchRequest; i++) {
					const fetchStart = performance.now();
					const resp = await this.fetch(`${this.config.verityOrigin}/api/verity/connection/check`, {
						method: "POST",
						body: data,
						signal: controller.signal,
					});
					if (resp.ok) {
						lastFetchEndTime = performance.now();
						lastFetchDuration = lastFetchEndTime - fetchStart;
						uploadSuccessCount++;
					}
				}
			} catch (err) {
				if (!controller.signal.aborted) {
					throw err;
				}
			}
			const endTime = performance.now();
			const remainingTime = endTime - lastFetchEndTime;

			// If fetch was aborted, calculate the remaining bits uploaded based on ratio of time it took for a successful fetch to the time used for partial fetch.
			// If lastFetchDuration is 0, user wasnt'able to complete 1 fetch so we will return 0 since we are unable to estimate their speed
			const partialRatio = lastFetchDuration === 0 ? 0 : remainingTime / lastFetchDuration;

			// Total bytes uploaded = successful fetches + partial fetch.
			const uploadedBytes = uploadSuccessCount * smallByteSize + partialRatio * smallByteSize;

			// Multiply by 8 to convert to bits. Divide time by 1000 since time is in ms.
			return (uploadedBytes * 8) / ((endTime - startTime) / 1000);
		}
	}

	/**
	 * This connection check sucks
	 */
	check = async (uploadByteSize?: number, timeout?: number, numFetchRequest?: number) => {
		return {
			metrics: {
				//@ts-ignore
				type: navigator.connection?.type,
				//@ts-ignore
				effectiveType: navigator.connection?.effectiveType,
				//@ts-ignore
				downlinkMax: navigator.connection?.downlinkMax,
				//@ts-ignore
				downlink: navigator.connection?.downlink,
				//@ts-ignore
				rtt: navigator.connection?.rtt,
			},
			uploadSpeed: await this.#badConnectionCheck(uploadByteSize, timeout, numFetchRequest),
		};
	};
}
